import { RawNodeDatum } from 'react-d3-tree/lib/types'
import { NodeType, TreeNode } from '../../../types/tree'

const MAX_CONSEQUTIVE_SAME_TYPE_NODES = 5
const MAX_NODES_BEFORE_SHRINK = 10

export function nodeWithAllAttributes(node: TreeNode): RawNodeDatum {
  return {
    name: node.type === NodeType.other ? node.action : node.type,
    attributes: {
      action: node.action,
      interesting: node.interesting,
      source: node.source,
      icon: iconFromNode(node),
      original: node.original as any,
      ...node.extra,
    },
    ...(node.verdictInfo ? { verdictInfo: node.verdictInfo } : {}),
  }
}

function countMatchingNames(prevName: string, currentName: string, initialCount: number): number {
  let count = initialCount
  if (!prevName || prevName === currentName) {
    count++
  } else {
    count = 1
  }
  return count
}

/**
 * Adds a child, or groups repeated ones
 */
function appendOrGroupChild(children: RawNodeDatum[], newNode: RawNodeDatum, count: number) {
  if (count < MAX_CONSEQUTIVE_SAME_TYPE_NODES) {
    children.push(newNode)
  } else if (count === MAX_CONSEQUTIVE_SAME_TYPE_NODES) {
    children.push({
      name: newNode.name,
      attributes: { nodeId: newNode.attributes?.nodeId as string, hiddenCount: 1 },
    })
  } else {
    // Increment hidden count of the last hide node if node count exceeds MAX_CONSEQUTIVE_SAME_TYPE_NODES
    // Note: last is a reference to the object itself and not a copy, hence the update works
    const last = children[children.length - 1]
    if (typeof last.attributes?.hiddenCount === 'number') {
      last.attributes.hiddenCount++
    }
  }
}

export function buildRawNodeDatum(node: TreeNode, node2RawNode: (treeNode: TreeNode) => RawNodeDatum) {
  const data = node2RawNode(node)

  if (node.childs.length) {
    const noShrink = node.childs.length < MAX_NODES_BEFORE_SHRINK
    data.children = []
    let count = 0
    let prevName = ''

    for (const item of node.childs) {
      const newNode = buildRawNodeDatum(item, node2RawNode)

      if (noShrink) {
        data.children.push(newNode)
        continue
      }

      count = countMatchingNames(prevName, newNode.name, count)

      appendOrGroupChild(data.children, newNode, count)

      prevName = newNode.name
    }
  }

  return data
}

export function refineRawNodeDatum(node: RawNodeDatum): void {
  let prevNode: RawNodeDatum | null = null
  if (node.children?.length) {
    const children: RawNodeDatum[] = []
    for (const item of node.children) {
      refineRawNodeDatum(item)

      if (
        prevNode?.name === 'file' &&
        item.name === 'file' &&
        item.attributes?.File &&
        item.attributes?.File === prevNode.attributes?.File &&
        prevNode.attributes?.action === 'WriteTextFile' &&
        item.attributes?.action === 'ShellCreatedFile'
      ) {
        children.pop()
      }

      children.push(item)
      prevNode = item
    }

    node.children = children
  }
}

export function respondToVisibility(element: Element, callback: (flag: boolean) => void): IntersectionObserver {
  const options = {
    root: document.documentElement,
    threshold: 0.5,
  }

  function observerCallback(entries: IntersectionObserverEntry[]) {
    const entry = entries[0]
    callback(entry.isIntersecting)
  }

  const observer = new IntersectionObserver(observerCallback, options)

  observer.observe(element)
  return observer
}

export function exportCanvasAsPNG(
  svg: SVGElement,
  width: number,
  height: number,
  canvas: HTMLCanvasElement,
  fileName: string
): void {
  svg.setAttribute('width', `${width}`)
  svg.setAttribute('height', `${height}`)
  canvas.width = width
  canvas.height = height
  const data = new XMLSerializer().serializeToString(svg)
  const win = window.URL || window.webkitURL || window
  const img = new Image()
  img.crossOrigin = 'anonymous'
  const blob = new Blob([data], { type: 'image/svg+xml' })
  const url = win.createObjectURL(blob)

  img.onload = function () {
    canvas.getContext('2d')?.drawImage(img, 0, 0)
    win.revokeObjectURL(url)
    const uri = canvas.toDataURL('image/png').replace('image/png', 'octet/stream')
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.setAttribute('style', 'display: none')
    a.href = uri
    a.download = fileName
    a.click()
    window.URL.revokeObjectURL(uri)
    document.body.removeChild(a)
  }

  img.src = url
}

function iconFromNode(node: TreeNode): string {
  if (node.type === NodeType.process) {
    const prog = node.extra.Program?.toLowerCase()
    switch (prog) {
      case 'winword':
        return '/assets/icons/word.ico'
      case 'powershell':
        return '/assets/icons/powershell.ico'
      case 'excel':
        return '/assets/icons/excel.ico'
      case 'wordpad':
        return '/assets/icons/wordpad.ico'
      case 'powerpnt':
        return '/assets/icons/powerpoint.ico'
      default:
        return '/assets/icons/default.ico'
    }
  }

  return ''
}
